// Client-side Google Maps link parsing + reverse geocoding, for the Today
// page's remote (field-work) punch panel.
//
// Why client-side rather than reusing POST /api/hr/work_zones/parse_maps_url
// (internal/mapsurl): that endpoint is HR-gated and follows short-link
// redirects server-side. The remote punch panel is used by every employee
// and only needs the coordinates already embedded in the pasted text, so it
// parses locally and never blocks the punch on a network round-trip.
//
// Nothing here is a security boundary — a remote punch is a *declared*
// location by design (the server skips the geofence for it). The parsed
// name/area are display text the user can edit before punching.

export interface ParsedMapsLink {
  lat: number | null;
  lng: number | null;
  /** Place name lifted from the URL itself (/maps/place/<Name>/ or ?q=<name>), if any. */
  name: string;
}

const NUM = String.raw`-?\d+(?:\.\d+)?`;

// Priority order matters, and matches internal/mapsurl's: the !3d/!4d place
// pin is the actual point of interest, while @lat,lng is only wherever the
// map viewport happened to be panned — they can differ by kilometres.
const PIN_3D_4D = new RegExp(String.raw`!3d(${NUM})!4d(${NUM})`);
const PIN_1D_2D = new RegExp(String.raw`!2d(${NUM})!3d(${NUM})`); // note: !2d is lng, !3d is lat
const AT_VIEWPORT = new RegExp(String.raw`@(${NUM}),(${NUM})`);
const Q_COORDS = new RegExp(String.raw`[?&](?:q|query|ll|center|daddr)=(${NUM})(?:,|%2C)\s*(${NUM})`, 'i');

const PLACE_NAME = /\/maps\/place\/([^/@?]+)/;
const Q_NAME = /[?&](?:q|query)=([^&]+)/;

export function isGoogleMapsLink(raw: string): boolean {
  const s = raw.trim().toLowerCase();
  return (
    s.includes('google.com/maps') ||
    s.includes('maps.google.com') ||
    s.includes('maps.app.goo.gl') ||
    s.includes('goo.gl/maps')
  );
}

function validCoords(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

function decode(s: string): string {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' ')).trim();
  } catch {
    return s.replace(/\+/g, ' ').trim();
  }
}

/**
 * parseMapsLink pulls coordinates and (where present) a place name out of a
 * pasted Google Maps URL. Returns nulls for coords when nothing matches —
 * short links like maps.app.goo.gl/abc123 carry no coordinates until they
 * are resolved, which needs a redirect the browser can't follow
 * cross-origin, so the user is asked to paste the expanded link instead.
 */
export function parseMapsLink(raw: string): ParsedMapsLink {
  const url = raw.trim();
  let lat: number | null = null;
  let lng: number | null = null;

  for (const [re, latFirst] of [
    [PIN_3D_4D, true],
    [PIN_1D_2D, false],
    [Q_COORDS, true],
    [AT_VIEWPORT, true]
  ] as [RegExp, boolean][]) {
    const m = url.match(re);
    if (!m) continue;
    const a = Number(m[1]);
    const b = Number(m[2]);
    const [candLat, candLng] = latFirst ? [a, b] : [b, a];
    if (validCoords(candLat, candLng)) {
      lat = candLat;
      lng = candLng;
      break;
    }
  }

  let name = '';
  const placeMatch = url.match(PLACE_NAME);
  if (placeMatch) {
    name = decode(placeMatch[1]).replace(/\s+/g, ' ');
  } else {
    const qMatch = url.match(Q_NAME);
    // ?q= holding bare coordinates is a location, not a name.
    if (qMatch && !/^-?\d+(\.\d+)?(,|%2C)/.test(qMatch[1])) {
      name = decode(qMatch[1]).replace(/\s+/g, ' ');
    }
  }

  return { lat, lng, name };
}

export interface ReverseGeocoded {
  name: string;
  area: string;
}

export const FALLBACK_NAME = 'Pasted location';
export const FALLBACK_AREA = '—';

const NOMINATIM_TIMEOUT_MS = 5000;

// Nominatim's usage policy requires an identifying User-Agent. Browsers
// forbid setting User-Agent from fetch, so this goes out as a custom header
// the service also accepts; the request is a single, user-initiated lookup,
// well inside the 1 req/s policy.
const USER_AGENT = 'ToyamaTeams/1.0 (remote-punch; hr@toyamaworld.com)';

/**
 * reverseGeocode resolves lat/lng into a human-readable Name + Area via OSM
 * Nominatim. It never throws: on any failure — network error, non-200, or
 * the 5s timeout — it resolves to the fallback pair, because a remote punch
 * must stay possible with no connectivity to a third-party geocoder.
 */
export async function reverseGeocode(lat: number, lng: number): Promise<ReverseGeocoded> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), NOMINATIM_TIMEOUT_MS);
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`,
      { signal: controller.signal, headers: { Accept: 'application/json', 'User-Agent': USER_AGENT } }
    );
    if (!res.ok) return { name: FALLBACK_NAME, area: FALLBACK_AREA };
    return fromNominatim(await res.json());
  } catch {
    return { name: FALLBACK_NAME, area: FALLBACK_AREA };
  } finally {
    clearTimeout(timer);
  }
}

interface NominatimAddress {
  road?: string;
  pedestrian?: string;
  neighbourhood?: string;
  building?: string;
  amenity?: string;
  shop?: string;
  office?: string;
  suburb?: string;
  village?: string;
  town?: string;
  city?: string;
  city_district?: string;
  county?: string;
  state?: string;
  postcode?: string;
}

interface NominatimResponse {
  name?: string;
  display_name?: string;
  address?: NominatimAddress;
}

/**
 * fromNominatim derives Name (the specific place: a named POI, else the
 * road) and Area (suburb → city/town/village → state) from a jsonv2
 * response, falling back to the leading / trailing parts of display_name
 * when the structured address is too sparse. Exported for unit testing.
 */
export function fromNominatim(data: NominatimResponse): ReverseGeocoded {
  const a = data?.address ?? {};
  const parts = (data?.display_name ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const name =
    data?.name ||
    a.amenity ||
    a.building ||
    a.shop ||
    a.office ||
    a.road ||
    a.pedestrian ||
    a.neighbourhood ||
    parts[0] ||
    FALLBACK_NAME;

  const areaBits = [
    a.suburb || a.neighbourhood || a.city_district,
    a.city || a.town || a.village || a.county,
    a.state
  ].filter((s): s is string => Boolean(s) && s !== name);

  const area = areaBits.length > 0 ? [...new Set(areaBits)].join(', ') : parts.slice(1, 3).join(', ');

  return { name: name || FALLBACK_NAME, area: area || FALLBACK_AREA };
}
